/** Coaching routes (IA §2 Improve › Coaching, §3 the case page). Every view's state is in the URL. */
import { createRoute, lazyRouteComponent } from '@tanstack/react-router';
import { appRoute } from '@/app/appRoute';
import { isIsoDay } from '@/lib/timeRange';
import { str } from '@/lib/search';

export const CENTER_TABS = ['today', 'queue', 'calendar', 'cases', 'impact', 'coaches'] as const;
export type CenterTab = (typeof CENTER_TABS)[number];

export interface CenterSearch {
  tab?: CenterTab;
  /** Queue: the driver whose priority breakdown is open. */
  driver?: string;
  band?: 'urgent' | 'high' | 'medium' | 'low';
  nosession?: boolean;
  /** Calendar: Monday of the week shown (YYYY-MM-DD) and whose calendar. */
  week?: string;
  coach?: string;
  /** Calendar: the session being moved. */
  session?: string;
  /** Cases: board (by stage) or logbook (every session). */
  view?: 'board' | 'logbook';
  stage?: string;
  status?: string;
  /** Logbook: session type and Code 95 only. */
  type?: string;
  code95?: boolean;
  q?: string;
  sort?: string;
  /** Impact: metric and the month of the time report. */
  metric?: 'safety' | 'eco' | 'fuel_l_100km';
  /** Impact: analysis window in days (90, 180, 365). */
  window?: '90' | '180' | '365';
  month?: string;
}

const BANDS = ['urgent', 'high', 'medium', 'low'] as const;
const METRICS = ['safety', 'eco', 'fuel_l_100km'] as const;

export const centerRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/coaching',
  staticData: { permission: ['coaching.session.read', 'coaching.case.read', 'coaching.priority.read'] },
  validateSearch: (s: Record<string, unknown>): CenterSearch => ({
    tab: (CENTER_TABS as readonly string[]).includes(String(s.tab)) ? (s.tab as CenterTab) : undefined,
    driver: str(s.driver),
    band: (BANDS as readonly string[]).includes(String(s.band)) ? (s.band as CenterSearch['band']) : undefined,
    nosession: s.nosession === true || s.nosession === 'true' ? true : undefined,
    week: isIsoDay(s.week) ? s.week : undefined,
    coach: str(s.coach),
    session: str(s.session),
    view: s.view === 'logbook' || s.view === 'board' ? s.view : undefined,
    stage: str(s.stage),
    status: str(s.status),
    type: str(s.type),
    code95: s.code95 === true || s.code95 === 'true' ? true : undefined,
    q: str(s.q),
    sort: str(s.sort),
    metric: (METRICS as readonly string[]).includes(String(s.metric)) ? (s.metric as CenterSearch['metric']) : undefined,
    window: ['90', '180', '365'].includes(String(s.window)) ? (String(s.window) as CenterSearch['window']) : undefined,
    month: typeof s.month === 'string' && /^\d{4}-\d{2}$/.test(s.month) ? s.month : undefined,
  }),
  component: lazyRouteComponent(() => import('./center/CoachingCenterPage')),
});

export const CASE_TABS = ['evidence', 'prep', 'sessions', 'messages', 'outcome'] as const;
export type CaseTab = (typeof CASE_TABS)[number];

export const caseRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/coaching/cases/$caseId',
  staticData: { permission: 'coaching.case.read' },
  validateSearch: (s: Record<string, unknown>): { tab?: CaseTab } => ({
    tab: (CASE_TABS as readonly string[]).includes(String(s.tab)) ? (s.tab as CaseTab) : undefined,
  }),
  component: lazyRouteComponent(() => import('./case/CasePage')),
});

/** A coach's profile and availability; `me` is the signed-in coach. */
export const coachRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/coaching/coaches/$userId',
  staticData: { permission: ['coaching.coach.read', 'coaching.session.schedule'] },
  component: lazyRouteComponent(() => import('./coaches/CoachPage')),
});
